import { useCallback, useEffect } from 'react';
import { useStore, StoreTypes } from 'context';
import { EventBus, EventBusType } from 'events/EventBus';
import { ReaderEvent, ReaderToolsEvent } from 'events/EventTypes';
import { ReaderZoomType, ReaderToolType } from 'constants/ReaderTools';
import * as types from 'constants/actionTypes';

/**
 * 小視窗的縮放行為（四格放大 / 區間放大 / 原尺寸）。
 *
 * 這三個功能原本內嵌在 ExtendedContentContainer，只有走那個容器的小視窗才有。
 * HTML 內開視窗走的是 ExtendedHtmlContentContainer，因此完全沒有縮放能力
 * （NBK-1639）。這裡把行為抽成共用 hook 供後者使用。
 *
 * ⚠️ ExtendedContentContainer 目前仍保留它自己的同款實作，尚未改用本 hook。
 *    本 hook 的內容是自該處逐字搬移，兩者行為相同。之所以不一併改，是因為
 *    ExtendedContentContainer 為二十餘種小視窗共用，改動風險遠大於此次需求；
 *    待本 hook 在 HTML 視窗上驗證穩定後，再以獨立 commit 收斂成單一實作。
 *
 * 呼叫端需自行：
 *   1. 依 drawAreaInfo.isDrawArea 渲染 <DrawArea />（區間放大的框選層）
 *   2. 以 <ExtendedPinchPanContainer> 包住內容容器（真正執行 transform 的地方）
 *
 * @param {object}  params
 * @param {React.RefObject} params.contentView - 內容容器的 ref，用於量測可視尺寸
 * @param {string}  params.eventBusType        - 事件匯流排類型
 */
export const useExtendedZoom = ({ contentView, eventBusType }) => {
  const [, extendedContentDispatch] = useStore(StoreTypes.extendedContent);

  const handleExtendedDrawAreaEvent = useCallback(({ drawAreaInfo }) => {
    extendedContentDispatch({ type: types.TOGGLE_DRAW_AREA, drawAreaInfo });
  }, [extendedContentDispatch]);

  const handleExtendedAreaZoomEvent = useCallback(({ type, rect }) => {
    if (!rect) return;
    
    // 確保座標值總是從左上到右下
    if (rect.originWidth < 0) {
      rect.originX += rect.originWidth;
      rect.originWidth = Math.abs(rect.originWidth);
    }
    if (rect.originHeight < 0) {
      rect.originY += rect.originHeight;
      rect.originHeight = Math.abs(rect.originHeight);
    }

    const container = contentView.current;
    const containerWidth = container.offsetWidth;
    const containerHeight = container.offsetHeight;

    // 根據容器和選取區域的寬高比來決定縮放比例
    let newScale = containerHeight / rect.originHeight;
    if (containerWidth / containerHeight < rect.originWidth / rect.originHeight) {
      newScale = containerWidth / rect.originWidth;
    }

    // 限制最大縮放倍率
    const maxScale = Math.min(newScale, 8);

    // 計算中心點位置
    const centerX = rect.originX + rect.originWidth * 0.5;
    const centerY = rect.originY + rect.originHeight * 0.5;

    // 計算新的偏移量，使選取區域置中
    const newOffsetX = -(centerX * maxScale) + containerWidth / 2;
    const newOffsetY = -(centerY * maxScale) + containerHeight / 2;

    // 更新縮放狀態
    extendedContentDispatch({
      type: types.SET_SCALE_INFO,
      scale: maxScale,
      offsetX: newOffsetX,
      offsetY: newOffsetY
    });

    // 縮放完成後切換回拖曳工具
    EventBus.emit({
      eventBusType,
      event: ReaderToolsEvent.ClickDragEvent
    });
    EventBus.emit({
      eventBusType,
      event: ReaderToolsEvent.SetReaderToolTypeEvent,
      payload: {
        toolType: ReaderToolType.Drag
      }
    });

  }, [contentView, extendedContentDispatch, eventBusType]);

  const handleExtendedReaderZoomEvent = useCallback((payload) => {
    const container = contentView.current;
    if (!container) return;

    const { type } = payload;
    
    if (type === ReaderZoomType.OriginZoom) {
      // 重置縮放
      extendedContentDispatch({
        type: types.SET_SCALE_INFO,
        scale: 1,
        offsetX: 0,
        offsetY: 0,
      });
    } else if (
      type === ReaderZoomType.LeftTop ||
      type === ReaderZoomType.RightTop ||
      type === ReaderZoomType.LeftBottom ||
      type === ReaderZoomType.RightBottom
    ) {
      // 四格放大的情況
      const containerWidth = container.offsetWidth;
      const containerHeight = container.offsetHeight;
      let offsetX = 0;
      let offsetY = 0;

      // 根據不同的角落設定不同的位移
      switch (type) {
        case ReaderZoomType.RightTop:
          offsetX = -containerWidth;
          break;
        case ReaderZoomType.LeftBottom:
          offsetY = -containerHeight;
          break;
        case ReaderZoomType.RightBottom:
          offsetX = -containerWidth;
          offsetY = -containerHeight;
          break;
        // LeftTop 就是預設的 (0, 0)
      }

      extendedContentDispatch({
        type: types.SET_SCALE_INFO,
        scale: 2,
        offsetX,
        offsetY,
      });
    }
  }, [contentView, extendedContentDispatch]);

  const SetFourZoomIn = useCallback((payload) => {
    
  }, [contentView, eventBusType]);

  useEffect(() => {
    const listeners = [
      [ReaderEvent.DrawAreaEvent, handleExtendedDrawAreaEvent],
      [ReaderEvent.AreaZoomEvent, handleExtendedAreaZoomEvent],
      [ReaderToolsEvent.SetReaderZoomEvent, handleExtendedReaderZoomEvent],
      [ReaderEvent.AreaZoomEvent, handleExtendedReaderZoomEvent],
      [ReaderToolsEvent.FourZoomIn, SetFourZoomIn],
    ];

    listeners.forEach(([event, handler]) =>
      EventBus.addListener({ eventBusType: EventBusType.ExtendedContent, event, handler }),
    );

    return () => {
      listeners.forEach(([event, handler]) =>
        EventBus.removeListener({ eventBusType: EventBusType.ExtendedContent, event, handler }),
      );
    };
    // 與 ExtendedContentContainer 原實作一致：只註冊一次，handler 以 ref 語意持有
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
